'use client'

import { useEffect } from 'react'
import { useTranslations } from 'next-intl'
import { useRouter } from '@/i18n/routing'
import { useConverterStore } from '@/stores/use-converter-store'
import { FileDropZone } from '@/components/shared/FileDropZone'
import { ToolStepIndicator } from '@/components/shared/ToolStepIndicator'
import { trackToolEvent } from '@/lib/utils/analytics'
import type { ConversionCategory } from '@/data/conversion-matrix'
import type { FileKind } from '@/stores/create-tool-store'

function detectFileKind(file: File): FileKind {
    const nameLower = file.name.toLowerCase()
    if (file.type.startsWith('image/') || /\.(jpg|jpeg|jfif|png|webp|gif|bmp|tif|tiff|ico|heic|heif|svg|avif)$/.test(nameLower)) return 'image'
    if (file.type.startsWith('video/') || /\.(mp4|mkv|webm|mov|avi|flv)$/.test(nameLower)) return 'video'
    if (file.type.startsWith('audio/') || /\.(mp3|wav|ogg|m4a|flac|aac|opus|wma)$/.test(nameLower)) return 'audio'
    return 'unknown'
}

interface ConvertPairUploadStepProps {
    targetFormat: string
    acceptedKind: ConversionCategory
    pairSlug: string
}

export function ConvertPairUploadStep({ targetFormat, acceptedKind, pairSlug }: ConvertPairUploadStepProps) {
    const t = useTranslations('Upload')
    const router = useRouter()
    const addFiles = useConverterStore(s => s.addFiles)
    const reset = useConverterStore(s => s.reset)
    const setFileKind = useConverterStore(s => s.setFileKind)
    const setTargetFormat = useConverterStore(s => s.setTargetFormat)

    useEffect(() => {
        setFileKind(acceptedKind)
        setTargetFormat(targetFormat)
    }, [targetFormat, acceptedKind, setFileKind, setTargetFormat])

    const handleFilesAdded = (files: File[]) => {
        reset()
        setFileKind(acceptedKind)
        setTargetFormat(targetFormat)

        addFiles(files, (f) => {
            const kind = detectFileKind(f)
            return kind === acceptedKind
        })

        const accepted = files.filter(f => detectFileKind(f) === acceptedKind)
        if (accepted.length > 0) {
            trackToolEvent('tool_upload_started', {
                tool: 'converter',
                file_count: accepted.length,
                file_kind: acceptedKind as FileKind,
                target_format: targetFormat,
                pair_slug: pairSlug,
            })
        }

        router.push(`/convert/${pairSlug}/configurar`)
    }

    const acceptString = (() => {
        switch (acceptedKind) {
            case 'image': return 'image/*,.heic,.heif,.svg,.avif,.jfif,.tif,.tiff'
            case 'video': return 'video/*'
            case 'audio': return 'audio/*,.opus,.wma'
            default: return 'image/*,video/*,audio/*'
        }
    })()

    return (
        <div className="mx-auto max-w-4xl">
            <ToolStepIndicator currentStep={1} baseUrl={`/convert/${pairSlug}`} />
            <div className="rounded-3xl bg-white p-4 shadow-2xl shadow-slate-200/50">
                <FileDropZone
                    accept={acceptString}
                    icon="auto_awesome"
                    title={t('selectFiles')}
                    subtitle={t('subtitleConverter')}
                    onFilesAdded={handleFilesAdded}
                />
            </div>
        </div>
    )
}
